import { isAxiosError } from "axios";

/**
 * Forget the secrets a failed Axios request carries before the error reaches a
 * `catch`: the request body (for a phone-verify call, the one-time code the
 * user just typed), the `Authorization` header (the session JWT on native), the
 * transport request, and any `set-cookie` response header. None may reach a
 * log (CASA 6.5.1).
 *
 * Two kinds of serializer see these errors. Sentry, and on the web the console
 * forwarders (Sentry Logs, Statsig console capture), go through the error's own
 * `toJSON`, which includes `config` — body and headers. React Native's console
 * inspector, which feeds Metro and the device log, instead walks enumerable
 * properties, reaching the transport request, where RN's XHR keeps the sent
 * headers in `_headers`, and the response headers. Nothing reads any of these
 * back off an error, so dropping them here makes every later log line safe
 * without each call site having to remember.
 *
 * @param error Any rejected value. An Axios error is scrubbed in place; anything
 *   else is left alone.
 */
export function dropAxiosRequestSecrets(error: unknown): void {
  if (!isAxiosError(error)) return;

  // The same object on an XHR rejection; a separate one elsewhere.
  for (const config of [error.config, error.response?.config]) {
    if (!config) continue;
    config.data = undefined;
    deleteHeader(config.headers, "Authorization");
  }

  error.request = undefined;
  if (error.response) {
    error.response.request = undefined;
    deleteHeader(error.response.headers, "set-cookie");
  }
}

/**
 * Delete a header by case-insensitive name. `AxiosHeaders` keeps each header as
 * an own property, so this works on it and on a plain header object alike, and
 * never throws — a throw here would replace the rejection the interceptor is
 * handling and skip its auth handling.
 */
function deleteHeader(headers: unknown, name: string): void {
  if (!headers || typeof headers !== "object") return;
  const lowerName = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lowerName) Reflect.deleteProperty(headers, key);
  }
}
